import React, { useState } from 'react'
import Topbar from '../components/Layout/Topbar'
import { Sidebar } from '../components/Layout/Sidebar'
import { Button, Input, Select, Option, Radio } from '@material-tailwind/react'
import { supabase, getUsersByRole } from '../supabase.config'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { saveOrderSupabase } from '../actions/orderAction'
import { useDispatch } from 'react-redux'
import { FaDownload, FaUpload, FaFileExcel, FaFileCsv } from 'react-icons/fa'
import Loader from '../components/Loader'
import { pinCodeList } from '../data/pinCodeData'

// LR counter for unique number generation
let lrCounter = 0

const BulkOrderUpload = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [uploadedData, setUploadedData] = useState([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [userList, setUserList] = useState([])
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedClient, setSelectedClient] = useState('')
  const [lrMode, setLrMode] = useState('automatic')
  const selectedClientData = userList.find(u => u.id === selectedClient)

  const normalizeHeader = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')

  const getCellValue = (row, keys) => {
    const rowEntries = Object.entries(row || {})
    for (const key of keys) {
      const normalizedKey = normalizeHeader(key)
      const matchedEntry = rowEntries.find(([rowKey]) => normalizeHeader(rowKey) === normalizedKey)
      const value = matchedEntry ? matchedEntry[1] : undefined
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value
      }
    }
    return ''
  }

  const parseCreatedAt = (value) => {
    if (!value) return null
    if (value instanceof Date) return value

    if (typeof value === 'number' && Number.isFinite(value)) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30))
      return new Date(excelEpoch.getTime() + value * 86400000)
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed) return null
      const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (dateOnlyMatch) return new Date(`${trimmed}T00:00:00`)
      const parsed = new Date(trimmed)
      if (!Number.isNaN(parsed.getTime())) return parsed
    }

    return null
  }

  // Fetch users for mapping client names
  const fetchUsers = async () => {
    const { data, error } = await getUsersByRole('client', 'logistics')
    if (error) {
      console.error('Failed to fetch users', error)
      return
    }
    const users = (data || []).map(u => ({
      id: u.id,
      name: u.name,
      email: u.email,
      companyName: u.company_name,
      uid: u.id
    }))
    setUserList(users)
  }

  React.useEffect(() => {
    fetchUsers()
  }, [])

  // Get city and state from pincode
  const getCityStateFromPincode = (pincode) => {
    if (!pincode) return { city: '', state: '' }
    const pin = Number(pincode)
    const found = pinCodeList.find(x => Number(x.Pin) === pin)
    if (found) {
      return {
        city: found.FacilityCity || '',
        state: found.FacilityState || ''
      }
    }
    return { city: '', state: '' }
  }

  // Generate and download sample Excel format (simplified)
  const downloadSampleCSV = () => {
    // Get selected client details
    const client = selectedClientData
    const clientEmail = client?.email || 'client@example.com'
    const clientCompany = client?.companyName || 'ABC Company'
    const sampleDate = new Date().toISOString().slice(0, 10)

    const sampleRow = {
      'Client Email': clientEmail,
      'Order ID': 'ORD-001',
      'Pickup Company': clientCompany,
      'Pickup Address': '123 Main Street',
      'Pickup Pincode': '400001',
      'Delivery Company': 'XYZ Corp',
      'Delivery Address': '456 Park Avenue',
      'Delivery Pincode': '110001',
      'Weight (kg)': '5',
      'Length (cm)': '30',
      'Width (cm)': '20',
      'Height (cm)': '15',
      'Quantity': '2',
      'Invoice Number': 'INV-001',
      'Invoice Amount': '5000',
      'Created At': sampleDate
    }

    if (lrMode === 'manual') {
      sampleRow['Manual LR'] = 'LR-001'
    }

    const sampleData = [sampleRow]

    const ws = XLSX.utils.json_to_sheet(sampleData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Sample')
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 30 }, { wch: 15 },
      { wch: 20 }, { wch: 30 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 20 }, { wch: 15 }, { wch: 15 }
    ]
    if (lrMode === 'manual') {
      colWidths.push({ wch: 18 })
    }
    ws['!cols'] = colWidths

    const filePrefix = lrMode === 'manual' ? 'Bulk_Order_Manual_LR_Sample' : 'Bulk_Order_Automatic_LR_Sample'
    const fileName = client ? `${filePrefix}_${clientCompany.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx` : `${filePrefix}.xlsx`
    XLSX.writeFile(wb, fileName)
    toast.success('Sample file downloaded!')
  }

  // Generate and download sample CSV format (simplified)
  const downloadSampleCSVFormat = () => {
    // Get selected client details
    const client = selectedClientData
    const clientEmail = client?.email || 'client@example.com'
    const clientCompany = client?.companyName || 'ABC Company'
    const sampleDate = new Date().toISOString().slice(0, 10)

    const headers = [
      'Client Email', 'Order ID', 'Pickup Company', 'Pickup Address', 'Pickup Pincode',
      'Delivery Company', 'Delivery Address', 'Delivery Pincode',
      'Weight (kg)', 'Length (cm)', 'Width (cm)', 'Height (cm)', 'Quantity',
      'Invoice Number', 'Invoice Amount', 'Created At'
    ]

    if (lrMode === 'manual') {
      headers.push('Manual LR')
    }

    const sampleRow = [
      clientEmail, 'ORD-001', clientCompany, '123 Main Street', '400001',
      'XYZ Corp', '456 Park Avenue', '110001',
      '5', '30', '20', '15', '2',
      'INV-001', '5000', sampleDate
    ]

    if (lrMode === 'manual') {
      sampleRow.push('LR-001')
    }

    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const filePrefix = lrMode === 'manual' ? 'Bulk_Order_Manual_LR_Sample' : 'Bulk_Order_Automatic_LR_Sample'
    const fileName = client ? `${filePrefix}_${clientCompany.replace(/[^a-zA-Z0-9]/g, '_')}.csv` : `${filePrefix}.csv`
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('Sample CSV file downloaded!')
  }

  // Parse CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = []
    let current = ''
    let inQuotes = false
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i++ // Skip next quote
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    result.push(current.trim())
    return result
  }

  // Handle file upload and parse
  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (!file) {
      toast.error('No file selected!')
      return
    }

    // Validate file type
    const validExtensions = ['.xlsx', '.xls', '.csv']
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file!')
      return
    }

    setSelectedFile(file)
    setLoading(true)
    setUploadedData([]) // Clear previous data
    
    const reader = new FileReader()

    reader.onerror = () => {
      toast.error('Error reading file!')
      setLoading(false)
    }

    reader.onload = (e) => {
      try {
        let jsonData = []
        
        if (file.name.toLowerCase().endsWith('.csv')) {
          // Handle CSV file with proper parsing
          const text = e.target.result
          const lines = text.split(/\r?\n/).filter(line => line.trim())
          
          if (lines.length < 2) {
            toast.error('CSV file must have at least a header and one data row!')
            setLoading(false)
            return
          }
          
          // Parse header
          const headers = parseCSVLine(lines[0]).map(h => h.trim().replace(/^"|"$/g, ''))
          
          // Parse data rows
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue // Skip empty lines
            
            const values = parseCSVLine(lines[i]).map(v => v.trim().replace(/^"|"$/g, ''))
            if (values.length !== headers.length) {
              console.warn(`Row ${i + 1} has ${values.length} columns but expected ${headers.length}`)
            }
            
            const row = {}
            headers.forEach((header, index) => {
              row[header] = values[index] || ''
            })
            jsonData.push(row)
          }
        } else {
          // Handle Excel file
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            
            if (workbook.SheetNames.length === 0) {
              toast.error('Excel file has no sheets!')
              setLoading(false)
              return
            }
            
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
            jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' })
          } catch (excelError) {
            console.error('Excel parsing error:', excelError)
            toast.error('Error parsing Excel file. Please check the format.')
            setLoading(false)
            return
          }
        }

        if (jsonData.length === 0) {
          toast.error('File is empty!')
          setLoading(false)
          return
        }

        // Validate and map the data (simplified)
        const mappedData = jsonData.map((row, index) => {
          // Find user by email
          const userEmail = getCellValue(row, ['Client Email', 'client email', 'ClientEmail', 'Client email'])
          let user = null
          if (userEmail) {
            user = userList.find(u => u.email?.toLowerCase() === userEmail.toLowerCase())
          }

          // Get pickup pincode and fetch city/state
          const pickupPincode = getCellValue(row, ['Pickup Pincode', 'pickup pincode', 'PickupPincode'])
          const pickupLocation = getCityStateFromPincode(pickupPincode)

          // Get delivery pincode and fetch city/state
          const deliveryPincode = getCellValue(row, ['Delivery Pincode', 'delivery pincode', 'DeliveryPincode'])
          const deliveryLocation = getCityStateFromPincode(deliveryPincode)

          const createdAtRaw = getCellValue(row, ['Created At', 'created at', 'CreatedAt', 'createdAt', 'Created Date', 'created date'])
          const createdAt = parseCreatedAt(createdAtRaw)
          const manualLr = String(getCellValue(row, ['Manual LR', 'manual lr', 'ManualLR', 'manualLR', 'LR No', 'LR', 'lr no', 'lr']) || '').trim()

          return {
            rowNumber: index + 2, // Excel row number (1-indexed, +1 for header)
            user: user,
            orderID: getCellValue(row, ['Order ID', 'order id', 'OrderID', 'orderID']),
            pickup_location: {
              pickupName: {
                name: '',
                companyName: getCellValue(row, ['Pickup Company', 'pickup company', 'PickupCompany'])
              },
              address: getCellValue(row, ['Pickup Address', 'pickup address', 'PickupAddress']),
              city: pickupLocation.city,
              state: pickupLocation.state,
              pinCode: pickupPincode || '',
              phone: ''
            },
            dropoff_location: {
              consignee: '',
              companyName: getCellValue(row, ['Delivery Company', 'delivery company', 'DeliveryCompany']),
              address: getCellValue(row, ['Delivery Address', 'delivery address', 'DeliveryAddress']),
              city: deliveryLocation.city,
              region: deliveryLocation.state,
              zip: deliveryPincode || '',
              phone: ''
            },
            weight: (() => {
              const kg = parseFloat(getCellValue(row, ['Weight (kg)', 'weight (kg)', 'Weight', 'weight']) || 0)
              if (kg) return kg
              const grams = parseFloat(getCellValue(row, ['Weight (grams)', 'weight (grams)']) || 0)
              return grams ? grams / 1000 : 0
            })(),
            length: parseFloat(getCellValue(row, ['Length (cm)', 'length (cm)', 'Length', 'length']) || 0),
            width: parseFloat(getCellValue(row, ['Width (cm)', 'width (cm)', 'Width', 'width']) || 0),
            height: parseFloat(getCellValue(row, ['Height (cm)', 'height (cm)', 'Height', 'height']) || 0),
            quantity: parseInt(getCellValue(row, ['Quantity', 'quantity', 'Qty', 'qty']) || 1),
            invoice: {
              ident: getCellValue(row, ['Invoice Number', 'invoice number', 'InvoiceNumber']),
              n_value: parseFloat(getCellValue(row, ['Invoice Amount', 'invoice amount', 'InvoiceAmount']) || 0),
              ewaybill: ''
            },
            paymentMode: 'Prepaid',
            expectedDate: '',
            createdAt,
            manualLr
          }
        })

        if (mappedData.length === 0) {
          toast.error('No valid data found in file!')
          setLoading(false)
          return
        }

        setUploadedData(mappedData)
        toast.success(`Successfully loaded ${mappedData.length} order(s) from file!`)
      } catch (error) {
        console.error('Error parsing file:', error)
        toast.error(`Error parsing file: ${error.message || 'Please check the format.'}`)
        setSelectedFile(null)
      } finally {
        setLoading(false)
      }
    }

    // Read file based on type
    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file, 'UTF-8')
    } else {
      reader.readAsArrayBuffer(file)
    }
  }

  // Generate LR number with unique counter
  const generateLrNo = () => {
    const now = new Date()
    const dateKey = `${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const ms = String(now.getTime()).slice(-3)
    lrCounter++
    const uniqueId = String(lrCounter).padStart(4, '0')
    return `${dateKey}${ms}${uniqueId}`
  }

  // Create all orders
  const createAllOrders = async () => {
    if (uploadedData.length === 0) {
      toast.error('No data to create orders!')
      return
    }

    // Validate all rows
    const errors = []
    uploadedData.forEach((row, index) => {
      if (!row.user) {
        errors.push(`Row ${row.rowNumber}: Client not found (check Client Email)`)
      }
      if (!row.pickup_location.address) {
        errors.push(`Row ${row.rowNumber}: Missing pickup address`)
      }
      if (!row.pickup_location.pinCode) {
        errors.push(`Row ${row.rowNumber}: Missing pickup pincode`)
      }
      if (!row.pickup_location.city || !row.pickup_location.state) {
        errors.push(`Row ${row.rowNumber}: City/State not found for pickup pincode ${row.pickup_location.pinCode}`)
      }
      if (!row.dropoff_location.address) {
        errors.push(`Row ${row.rowNumber}: Missing delivery address`)
      }
      if (!row.dropoff_location.zip) {
        errors.push(`Row ${row.rowNumber}: Missing delivery pincode`)
      }
      if (!row.dropoff_location.city || !row.dropoff_location.region) {
        errors.push(`Row ${row.rowNumber}: City/State not found for delivery pincode ${row.dropoff_location.zip}`)
      }
      if (!row.weight || row.weight <= 0) {
        errors.push(`Row ${row.rowNumber}: Invalid weight`)
      }
      if (!row.length || !row.width || !row.height || row.length <= 0 || row.width <= 0 || row.height <= 0) {
        errors.push(`Row ${row.rowNumber}: Invalid dimensions`)
      }
      if (lrMode === 'manual' && !row.manualLr) {
        errors.push(`Row ${row.rowNumber}: Missing Manual LR`)
      }
    })

    if (errors.length > 0) {
      toast.error(`Please fix ${errors.length} error(s) before creating orders`)
      console.error('Validation errors:', errors)
      return
    }

    setCreating(true)
    const date = new Date()
    let successCount = 0
    let failCount = 0
    lrCounter = 0 // Reset counter for this batch

    try {
      for (const row of uploadedData) {
        try {
          const lrno = lrMode === 'manual' ? row.manualLr : generateLrNo()
          const chargeableWeight = Math.ceil((row.length * row.width * row.height / 4500) * row.quantity)
          
          const createdAt = row.createdAt || date
          const orderData = {
            ident: "",
            createdAt,
            pickup_location: row.pickup_location,
            dropoff_location: row.dropoff_location,
            return_address: row.dropoff_location,
            d_mode: row.paymentMode === 'COD' ? 'COD' : 'Prepaid',
            purchaseCharge: 0,
            salesCharge: 0,
            orderStatus: "new",
            payment_mode: row.paymentMode || 'Prepaid',
            rov_insurance: false,
            invoices: row.invoice.ident ? [row.invoice] : [],
            weight: row.weight,
            suborders: [],
            dimensions: [{
              length: row.length,
              width: row.width,
              height: row.height,
              count: row.quantity
            }],
            consignee_gst_tin: "",
            seller_gst_tin: "",
            cb: {},
            totalqty: row.quantity,
            orderID: row.orderID || "",
            lrno: lrno,
            totalChargeableWeight: chargeableWeight,
            masterAWB: "",
            uid: row.user.id,
            userName: row.user.companyName || row.user.name,
            deliveryactualdate: row.expectedDate || null,
            orderActivity: [{
              status: "Order Created",
              remark: "Order has been Created via Bulk Upload",
              location: "",
              time: createdAt
            }]
          }

          await dispatch(saveOrderSupabase(orderData))
          successCount++
        } catch (error) {
          console.error(`Error creating order for row ${row.rowNumber}:`, error)
          failCount++
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully created ${successCount} order(s)!`)
        setTimeout(() => {
          navigate('/orders')
        }, 2000)
      }
      if (failCount > 0) {
        toast.error(`Failed to create ${failCount} order(s)`)
      }
    } catch (error) {
      console.error('Error creating orders:', error)
      toast.error('Error creating orders')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className='bg-gray-100 flex'>
      <Toaster />
      <Sidebar />
      <div className='h-[100vh] overflow-y-scroll flex flex-1 flex-col'>
        <Topbar />
        <div className='mx-4 my-6'>
          <div className='bg-white rounded-lg shadow-sm p-6'>
            {/* Header */}
            <div className='flex items-center justify-between mb-6'>
              <div>
                <h2 className='text-2xl font-[GilroyBold] text-gray-800'>Bulk Order Upload</h2>
                <p className='text-gray-600 mt-1'>Upload Excel or CSV file to create multiple orders at once</p>
              </div>
              <Button
                onClick={() => navigate('/orders')}
                variant='outlined'
                className='border-gray-300'
              >
                Back to Orders
              </Button>
            </div>

            {/* Download Sample Section */}
            <div className='bg-gray-50 rounded-lg p-4 mb-6'>
              <h3 className='text-lg font-[GilroyBold] mb-3'>Download Sample Format</h3>
              <div className='flex flex-col gap-4'>
                <div>
                  <p className='text-sm font-[GilroyMedium] text-gray-700 mb-2'>Bulk Upload LR Mode</p>
                  <div className='flex items-center gap-6 flex-wrap'>
                    <Radio
                      name='bulk-lr-mode'
                      label='Automatic LR'
                      value='automatic'
                      checked={lrMode === 'automatic'}
                      onChange={(e) => setLrMode(e.target.value)}
                    />
                    <Radio
                      name='bulk-lr-mode'
                      label='Manual LR'
                      value='manual'
                      checked={lrMode === 'manual'}
                      onChange={(e) => setLrMode(e.target.value)}
                    />
                  </div>
                  <p className='text-sm text-gray-600 mt-2'>
                    {lrMode === 'manual'
                      ? 'Manual mode requires a `Manual LR` column in the uploaded Excel/CSV file.'
                      : 'Automatic mode will generate LR numbers during bulk order creation.'}
                  </p>
                </div>
                <div className='w-full max-w-md'>
                  <Select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e)}
                    label='Select Client (Optional)'
                    className='font-[GilroyMedium]'
                    selected={(element) => {
                      if (!selectedClientData) return element
                      return (
                        <span className='block truncate'>
                          {selectedClientData.companyName || selectedClientData.name} ({selectedClientData.email})
                        </span>
                      )
                    }}
                  >
                    <Option value=''>None (Generic Sample)</Option>
                    {userList.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {user.companyName || user.name} ({user.email})
                      </Option>
                    ))}
                  </Select>
                  {selectedClient && (
                    <p className='text-sm text-gray-600 mt-2'>
                      Sample will be pre-filled with: {selectedClientData?.email}
                    </p>
                  )}
                </div>
                <div className='flex gap-3'>
                  <Button
                    onClick={downloadSampleCSV}
                    className='bg-[#FFB200] hover:bg-[#e6a000] text-black flex items-center gap-2'
                  >
                    <FaFileExcel className='w-4 h-4' />
                    Download Excel Sample
                  </Button>
                  <Button
                    onClick={downloadSampleCSVFormat}
                    className='bg-black hover:bg-gray-800 text-white flex items-center gap-2'
                  >
                    <FaFileCsv className='w-4 h-4' />
                    Download CSV Sample
                  </Button>
                </div>
                {!selectedClient && (
                  <p className='text-sm text-gray-600'>
                    ℹ️ No client selected - sample will use generic placeholder data. Select a client to pre-fill with their information.
                  </p>
                )}
              </div>
            </div>

            {/* Upload Section */}
            <div className='bg-gray-50 rounded-lg p-4 mb-6'>
              <h3 className='text-lg font-[GilroyBold] mb-3'>Upload File</h3>
              <div className='flex flex-col gap-4'>
                <div className='flex items-center gap-4 flex-wrap'>
                  <input
                    type='file'
                    accept='.xlsx,.xls,.csv'
                    onChange={handleFileUpload}
                    className='hidden'
                    disabled={loading}
                    id='file-upload-input'
                  />
                  <button
                    type='button'
                    onClick={() => {
                      if (!loading) {
                        document.getElementById('file-upload-input')?.click()
                      }
                    }}
                    disabled={loading}
                    className='bg-[#FFB200] hover:bg-[#e6a000] disabled:bg-gray-400 disabled:cursor-not-allowed text-black flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors'
                  >
                    <FaUpload className='w-4 h-4' />
                    {loading ? 'Processing...' : 'Choose File'}
                  </button>
                  {loading && <Loader />}
                  {selectedFile && !loading && (
                    <span className='text-gray-700 font-medium'>
                      Selected: {selectedFile.name}
                    </span>
                  )}
                  {uploadedData.length > 0 && (
                    <span className='text-green-600 font-medium'>
                      ✓ {uploadedData.length} order(s) loaded
                    </span>
                  )}
                </div>
                <p className='text-sm text-gray-500'>
                  Supported formats: Excel (.xlsx, .xls) or CSV (.csv). Maximum file size: 10MB
                </p>
                <div className='bg-orange-50 border border-orange-200 rounded p-3 mt-2'>
                  <p className='text-sm text-orange-800 font-medium mb-1'>Required Fields:</p>
                  <ul className='text-xs text-orange-700 list-disc list-inside space-y-1'>
                    <li>Client Email - Email of the client</li>
                    <li>Pickup Company, Address, Pincode - City & State auto-filled from pincode</li>
                    <li>Delivery Company, Address, Pincode - City & State auto-filled from pincode</li>
                    <li>Weight (kg), Dimensions (Length, Width, Height in cm), Quantity</li>
                    <li>Invoice Number, Invoice Amount (optional)</li>
                    {lrMode === 'manual' && <li>Manual LR - Required when Manual LR mode is selected</li>}
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Table */}
            {uploadedData.length > 0 && (
              <div className='mb-6'>
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='text-lg font-[GilroyBold]'>Preview Orders ({uploadedData.length})</h3>
                  <Button
                    onClick={createAllOrders}
                    className='bg-[#FFB200] hover:bg-[#e6a000] text-black'
                    disabled={creating}
                  >
                    {creating ? 'Creating Orders...' : `Create All Orders (${uploadedData.length})`}
                  </Button>
                </div>

                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse bg-white rounded-lg shadow-sm'>
                    <thead>
                      <tr className='bg-black text-white'>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Row</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Client</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Order ID</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>
                          {lrMode === 'manual' ? 'Manual LR' : 'LR'}
                        </th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Pickup</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Consignee</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Delivery</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Weight</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Dimensions</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Created At</th>
                        <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedData.map((row, index) => (
                        <tr
                          key={index}
                          className={`hover:bg-gray-50 ${!row.user ? 'bg-red-50' : ''}`}
                        >
                          <td className='border border-gray-300 px-4 py-2 text-sm'>{row.rowNumber}</td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            {row.user ? (
                              <div>
                                <div className='font-medium'>{row.user.companyName || row.user.name}</div>
                                <div className='text-xs text-gray-500'>{row.user.email}</div>
                              </div>
                            ) : (
                              <span className='text-red-600 font-medium'>Client not found</span>
                            )}
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            {row.orderID || '-'}
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            {lrMode === 'manual' ? (
                              row.manualLr ? row.manualLr : <span className='text-red-600 font-medium'>Missing</span>
                            ) : (
                              <span className='text-gray-500'>Auto-generated</span>
                            )}
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            <div className='text-xs'>
                              <div className='font-medium'>
                                {row.pickup_location.city || 'N/A'}, {row.pickup_location.state || 'N/A'}
                                {(!row.pickup_location.city || !row.pickup_location.state) && (
                                  <span className='text-red-500 ml-1'>⚠</span>
                                )}
                              </div>
                              <div className='text-gray-500'>{row.pickup_location.pinCode}</div>
                              <div className='text-xs'>{row.pickup_location.pickupName?.companyName}</div>
                            </div>
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            <div className='text-xs'>
                              <div className='font-medium'>{row.dropoff_location.companyName || 'N/A'}</div>
                            </div>
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            <div className='text-xs'>
                              <div className='font-medium'>
                                {row.dropoff_location.city || 'N/A'}, {row.dropoff_location.region || 'N/A'}
                                {(!row.dropoff_location.city || !row.dropoff_location.region) && (
                                  <span className='text-red-500 ml-1'>⚠</span>
                                )}
                              </div>
                              <div className='text-gray-500'>{row.dropoff_location.zip}</div>
                            </div>
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            {Number(row.weight || 0).toFixed(2)} kg
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            <div className='text-xs'>
                              {row.length}×{row.width}×{row.height} cm
                              <div className='text-gray-500'>Qty: {row.quantity}</div>
                            </div>
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            {row.createdAt
                              ? new Date(row.createdAt).toLocaleDateString('en-GB')
                              : '-'}
                          </td>
                          <td className='border border-gray-300 px-4 py-2 text-sm'>
                            {row.user && (lrMode !== 'manual' || row.manualLr) ? (
                              <span className='text-green-600 font-medium'>✓ Ready</span>
                            ) : (
                              <span className='text-red-600 font-medium'>✗ Error</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkOrderUpload
